import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Twitter, ShieldCheck } from "lucide-react";
import Logo from "@/components/brand/Logo";
import { STORE, DISCLAIMER } from "@/lib/store";

const shopLinks = [
  { label: "Shop All", to: "/shop" },
  { label: "Featured Products", to: "/shop?sort=featured" },
  { label: "Search", to: "/search" },
];

const companyLinks = [
  { label: "About Renova", to: "/about" },
  { label: "Quality Standards", to: "/quality-standards" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

const policyLinks = [
  { label: "Shipping Policy", to: "/shipping-policy" },
  { label: "Returns & Refunds", to: "/returns-policy" },
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Accessibility", to: "/accessibility" },
];

export default function Footer() {
  return (
    <footer className="bg-[#1E2A44] text-[#F7F4EF]" data-testid="site-footer">
      <div className="container-rnv py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Logo imgClass="h-16 md:h-20 w-auto" onNavy withLink />
            <p className="mt-6 text-sm leading-relaxed text-[#B9BCC4] max-w-sm">
              Renova Peptide Co. provides carefully presented research compounds for
              qualified laboratory and analytical applications, with a commitment to
              clarity and premium presentation.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <SocialIcon to="/contact" label="LinkedIn"><Linkedin size={18} /></SocialIcon>
              <SocialIcon to="/contact" label="Instagram"><Instagram size={18} /></SocialIcon>
              <SocialIcon to="/contact" label="X"><Twitter size={18} /></SocialIcon>
            </div>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-2">
            <FooterCol title="Shop" links={shopLinks} />
          </div>

          <div className="lg:col-span-2">
            <FooterCol title="Company" links={companyLinks} />
          </div>

          <div className="lg:col-span-4">
            <h4 className="overline !text-[#D8BE87] mb-4">Customer Service</h4>
            <p className="text-sm text-[#B9BCC4] leading-relaxed max-w-sm">
              Questions about a product or an order? Our team is here to help.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-gold !text-[#F7F4EF] !border-[#C8A96A] !py-2.5 !px-5 text-sm">Contact Us</Link>
              <a href={STORE.account} className="btn-gold !text-[#F7F4EF] !border-[#C8A96A] !py-2.5 !px-5 text-sm">My Account</a>
            </div>

            <div className="mt-8">
              <h4 className="overline !text-[#D8BE87] mb-4">Policies</h4>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-3">
                {policyLinks.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-[#B9BCC4] hover:text-[#D8BE87] transition-colors">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 flex items-center gap-2 text-xs text-[#8B90A0]">
              <ShieldCheck size={16} className="text-[#C8A96A]" />
              Secure checkout handled by our Shift4Shop store.
            </div>
          </div>
        </div>

        <div className="gold-rule mt-14 mb-8" />

        <div className="rounded-sm border border-[#C8A96A]/25 bg-[#111C32] p-5 md:p-6">
          <p className="text-xs leading-relaxed text-[#9aa0b0]" data-testid="footer-disclaimer">
            <span className="text-[#D8BE87] font-semibold uppercase tracking-wider">Research Disclaimer — </span>
            {DISCLAIMER}
          </p>
        </div>

        <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#8B90A0]">
          <p>© {new Date().getFullYear()} Renova Peptide Co. All rights reserved.</p>
          <a href={STORE.shop} className="hover:text-[#D8BE87] transition-colors">Visit our secure store →</a>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="overline !text-[#D8BE87] mb-4">{title}</h4>
      <ul className="space-y-3">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="text-sm text-[#B9BCC4] hover:text-[#D8BE87] transition-colors">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SocialIcon({ to, label, children }) {
  return (
    <Link
      to={to}
      aria-label={label}
      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-[#D8BE87] hover:border-[#C8A96A] hover:bg-white/5 transition-colors"
    >
      {children}
    </Link>
  );
}
